import { useCallback } from 'react'
import type { FormikHelpers } from 'formik'
import { useNavigate } from 'react-router-dom'

import { toast } from '@sales-center/components'

import {
  saveAndAutoReviewDraftLead,
  type DraftLead,
  type IAutoReviewMissingField,
} from '@user/store/draftLeads'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { isAPIError } from '@user/store/services/crud'

import type {
  AutoReviewLookups,
  UpdateRequiredFieldsFormModel,
} from '../typings'
import { buildAutoReviewInput } from '../utils/buildAutoReviewInput'
import { mapMissingFieldsToErrors } from '../utils/mapMissingFieldsToErrors'
import { getEditableMissingFields } from '../utils/nonEditableMissingFields'

type Props = {
  lead: DraftLead
  editableMissing: IAutoReviewMissingField[]
  lookups: AutoReviewLookups
  onClose: () => void
}

const FALLBACK_ERROR = 'Unable to auto-review this pending lead.'
const USE_REVIEW_MESSAGE =
  'This pending lead still has fields that cannot be set here. Use Review to complete it.'

export function useHandlers(props: Props) {
  const { lead, editableMissing, lookups, onClose } = props

  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const onSubmit = useCallback(
    async (
      values: UpdateRequiredFieldsFormModel,
      helpers: FormikHelpers<UpdateRequiredFieldsFormModel>,
    ) => {
      const input = buildAutoReviewInput(values, editableMissing, lookups)
      const result = await dispatch(
        saveAndAutoReviewDraftLead({ id: lead.id, input }),
      )

      if (saveAndAutoReviewDraftLead.rejected.match(result)) {
        toast.error(
          result.payload && isAPIError(result.payload)
            ? result.payload.Message
            : FALLBACK_ERROR,
        )
        return
      }

      const { converted, leadId, missingFields } = result.payload

      if (converted) {
        onClose()
        // The draft row survives flagged ConvertedToLead, so go to the new lead.
        if (leadId) navigate(`/leads/${leadId}`)
        return
      }

      const stillEditable = getEditableMissingFields(missingFields ?? [])
      if (stillEditable.length > 0)
        helpers.setErrors(
          mapMissingFieldsToErrors(stillEditable, lookups.customFields),
        )
      else toast.error(USE_REVIEW_MESSAGE)
    },
    [dispatch, editableMissing, lead.id, lookups, navigate, onClose],
  )

  return { onSubmit }
}
